
import { set, z } from "zod"
import {zodResolver} from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "./ui/input"
import { Button } from "./ui/button"
import { EditProfileProps, ProfileContext, ProfileFeaturedType } from "./Profile"
import { ArrowBigLeft, Clapperboard, Disc3Icon, Dot, FolderOpen, GripVertical, ListOrdered, Lock, Mic2, Music2, SearchX, SquarePlus, Tv, X } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';
import { FetchUserLists, PatchProfile } from "./ApiRoutes"
import {useContext, useEffect, useRef, useState } from "react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "./ui/label"
import { ScrollArea } from "./ui/scroll-area"
import apiClient from "@/utils/ApiClient"
import { QueryResult } from "./ListEditor"
import { truncateText } from "@/utils/TextUtils"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { useQuery } from "@tanstack/react-query"
import { SecondaryLoader } from "@/utils/Loaders"
import {
    DndContext, 
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
  } from '@dnd-kit/core';
  import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
  } from '@dnd-kit/sortable';
import {CSS as DNDCSS} from '@dnd-kit/utilities';
import { restrictToParentElement  } from '@dnd-kit/modifiers';
import { title } from "process"
import { UserContext } from "@/App"
import { toast, Toaster } from "sonner"


const MAX_FILE_SIZE = 5*1024*1024;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
 
const profileFormSchema = z.object({
    avatar: z
    .any()
    .refine(
      (file) =>
        !file ||
        (ACCEPTED_IMAGE_TYPES.includes(file.type)),
      "Only .jpg, .jpeg, .png and .webp formats are supported."
    )
    .refine(
      (file) =>
        !file ||
        (file.size <= MAX_FILE_SIZE),
      `Max image size is 5MB.`
    )
    .optional(),

    username: z.string().min(3,{
        message: "Username must be at least 3 characters long",
    }).max(25, {
        message: "Username must be at most 25 characters long",
    }),
    bio: z.string().max(50, {
        message: "Bio can only be 50 characters long",
    }).optional(),
    })

export function EditProfileForm({username, bio, backgroundImage}:EditProfileProps) {
    const form = useForm<z.infer<typeof profileFormSchema>>({
        resolver: zodResolver(profileFormSchema),
        defaultValues: {
            username: username || "",
            bio: bio || "",
            avatar: undefined,
            backgroundImage: backgroundImage,
        },
    })

    useEffect(() => {
        form.reset({
            username: username || "",
            bio: bio || "",
            avatar: undefined,
            backgroundImage: undefined,
        })
    }, [username, bio, backgroundImage, form.reset])

    async function onSubmit(data: z.infer<typeof profileFormSchema>) {
        const dirtyFields = form.formState.dirtyFields;

        if (Object.keys(dirtyFields).length === 0) {
            return;
        }

        if (dirtyFields.avatar) {
            const formData = new FormData();

            for (const key in dirtyFields) {
                if (key in data) {
                    const value = data[key as keyof typeof data];
                    if (value !== undefined && value !== null) {
                        formData.append(key, value);
                    }
                }
            }

        const result = await PatchProfile(formData);
        window.location.reload();
          
        } else {
            // NO files were changed, so we can send a simple JSON object
            const changedTextData: { [key: string]: any } = {};

            if (dirtyFields.bio) {
                changedTextData.bio = data.bio;
            }
            const result = await PatchProfile(changedTextData);

            window.location.reload();
        }
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2 mt-3">
                <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                    <FormItem className="hover:cursor-not-allowed">
                    <FormLabel>Username</FormLabel>
                    <FormControl className="relative">
                        <div className="flex items-center">
                            <Input disabled={true} placeholder={username} {...field} />
                            <Lock className="absolute text-neutral-600 right-2 w-5 h-5"/>
                        </div>
                    </FormControl>
                    <FormDescription>
                        This is your public display name.
                    </FormDescription>
                    <FormMessage />
                    </FormItem>
                )}
                />
                <FormField
                control={form.control}
                name="bio"
                render={({ field }) => (
                    <FormItem>
                    <FormLabel>Bio</FormLabel>
                    <FormControl>
                        <Input placeholder={bio} {...field} />
                    </FormControl>
                    <FormDescription>
                        A short blurb about yourself. (max 50 characters)
                    </FormDescription>
                    <FormMessage />
                    </FormItem>
                )}
                />
                <FormField
                control={form.control}
                name="avatar"
                render={({ field }) => (
                    <FormItem >
                    <FormLabel>Profile picture</FormLabel>
                    <FormControl className="relative ">
                        <div className="flex justify-center items-center ">
                        <Input
                            className="flex justify-start items-center hover:cursor-pointer"
                            type="file"
                            onChange={(e) => {
                                const file = e.target.files && e.target.files[0];
                                field.onChange(file);
                            }}
                            accept="image/jpeg, image/jpg, image/png, image/webp"
                        />
                        <FolderOpen className="absolute left-5 h-6 w-6"/>
                        </div>
                    </FormControl>
                    <FormDescription>
                        Upload a new profile picture. (max 5MB)
                    </FormDescription>
                    <FormMessage />
                    </FormItem>
                )}
                />
                <Button className="mt-2" type="submit">Submit</Button>
            </form>
        </Form>
    )
}

const customizeProfileFormSchema = z.object({
    backgroundImage: z.string().url({
        message: "Please enter a valid URL for the background image.",
    }).optional(),
    featuredItems: z.array(
        z.object({
            item_type: z.enum(["list","shows","movies","songs","artists","albums"]),
            internal_list_id: z.union([z.null(), z.number()]).optional(), 
            external_id: z.union([z.null(), z.string()]).optional(),
            title: z.string(),
            image: z.string().optional(),
            artist: z.union([z.null(), z.string()]).optional(),                                  
            year: z.union([z.null(), z.number()]).optional(),                                 
            comment: z.union([z.null(), z.string()]).optional(),
            order: z.number()
        })).max(3, { message: "You can feature a maximum of 3 items." })
    })

interface featuredItem {
    id:number,
    item_type:"list"|"movies"|"shows"|"songs"|"artists"|"albums",
    internal_list_id?:number|null,
    external_id?:string|null,
    title:string,
    image?:string,
    year?:number|null,
    comment?:string|null,
    artist?:string|null,
    order:number,    
}

export function CustomizeProfileForm(){
    const profileContext = useContext(ProfileContext);
    const [isMediaSearchOpen, setIsMediaSearchOpen] = useState(false);
    const [isFeatureSelectOpen, setIsFeatureSelectOpen ] = useState(false);
    const [itemsOpen, setItemsOpen] = useState("open");
    const [message, setMessage] = useState("");
    const [allFeatured, setAllFeatured] = useState<featuredItem[]>(profileContext?.featured_items.map(
        (item, index)=> resultToItem(item, (item.item_type == "list" ? "list" : "item"),item.order,true,(item.item_type != "list" ? item.item_type : undefined) )) ?? []);
    const [selectionType, setSelectionType] = useState("item");
    

    const form = useForm<z.infer<typeof customizeProfileFormSchema>>({
        resolver: zodResolver(customizeProfileFormSchema),
        defaultValues: {
            backgroundImage: undefined,
            featuredItems: [], 
        },
    })

    const handleSelectedBackground = (imageUrl: string) => {
        form.setValue('backgroundImage', imageUrl, {
            shouldValidate: true, 
            shouldDirty: true,    
        });
        setIsMediaSearchOpen(false); 
    };

    useEffect(() => {
        // reaffirm for backend view constraint (order is unique to item)
        const sortedFeatured = [...allFeatured].map((item, index) => ({
            ...item,
            order: index 
        }));

        form.setValue('featuredItems', sortedFeatured, {
            shouldValidate: true,
            shouldDirty: true, 
        });
    }, [allFeatured]);
    


    // when an item is selected (list or media) update state
    const onFeaturedSelect = (selection:featuredItem) => {

        if(allFeatured.length <= 2){
            setAllFeatured([...allFeatured,
                selection
            ]);
        }
        
        form.setValue('featuredItems', allFeatured, {
            shouldValidate: true, 
            shouldDirty: true,    
        });
    }


    async function onSubmit(data: z.infer<typeof customizeProfileFormSchema>) {
        const dirtyFields = form.formState.dirtyFields;
        const payloadToSubmit: { background?: string, featured_items?: featuredItem[] } = {};

        console.log("Dirty fields:", dirtyFields);
        if (dirtyFields.backgroundImage && data.backgroundImage !== undefined) {
            payloadToSubmit.background = data.backgroundImage;
        }

        if (dirtyFields.featuredItems) {
            payloadToSubmit.featured_items = data.featuredItems;
        } 

        if (Object.keys(payloadToSubmit).length > 0) {
            const result = await PatchProfile(payloadToSubmit); 

            if (result.status === 200) {
                window.location.reload();
            } 
            else {
                toast.error(result.status);
            }
        }

        else {
            toast.error("No changes to submit.");
        }
    }

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
          coordinateGetter: sortableKeyboardCoordinates,
        })
      );

    function handleDragEnd(event:DragEndEvent) {
    const {active, over} = event;

    if (active.id !== over.id) {
        setAllFeatured((items) => {
        // Find the actual array index of the items using their IDs
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);

        // Check if both indices were found before attempting to move
        if (oldIndex !== -1 && newIndex !== -1) {
            return arrayMove(items, oldIndex, newIndex);
        }
        // Return original items if indices weren't found (optional safety check)
        return items;
        });
    }
    }
     
    // render for customize profile form
    if(!isMediaSearchOpen && !isFeatureSelectOpen){
        return(
            <Form {...form}>
            
                <form onSubmit={form.handleSubmit(onSubmit, (errors) => console.log(errors))} className="space-y-4">
                    <FormItem className="mt-4">
                        <FormLabel>Background image</FormLabel>
                        <Button className="dark hover:cursor-pointer" type="button" variant="outline" onClick={() => setIsMediaSearchOpen(true)}>
                            Choose background
                        </Button>
                        {form.watch('backgroundImage') && (
                            <div className="mt-2">
                                <p className="text-sm text-muted-foreground">New background</p>
                                <img src={form.watch('backgroundImage')} alt="Selected background preview" className="w-full h-32 object-cover rounded-md" />
                            </div>
                        )}
                         
                        <FormDescription>
                            Choose a piece of media art to set as your profile background.
                        </FormDescription>
                        <FormMessage />
                    </FormItem>
                    <FormItem className="flex flex-col mt-4">
                        <FormLabel>Featured items</FormLabel>
                        <Button className="dark hover:cursor-pointer" type="button" variant="outline" onClick={() => setIsFeatureSelectOpen(true)}>
                           Manage featured items
                        </Button>
                        
                        <FormDescription>
                            Use your own created lists or search for media to feature on your profile.
                        </FormDescription>
                        {  form.watch('featuredItems') && allFeatured.length >= 1 &&
                            <></>
                        }
                        <FormMessage />
                    </FormItem>
                    

                    <Button className="w-full hover:cursor-pointer" type="submit">Save Changes</Button>
                </form>
            </Form>
        )
    }

    // render for background image
    else if(isMediaSearchOpen){
        return(
            <section className="flex flex-col gap-6">
                <MediaSearch usage="background" setIsMediaSearchOpen={setIsMediaSearchOpen}
                onImageSelected={handleSelectedBackground} setFeaturedOpen={setItemsOpen}/>
                
                <Button type="button" className="w-full" onClick={() => setIsMediaSearchOpen(false)}>
                    <ArrowBigLeft className="w-7 h-7 hover:cursor-pointer"/>
                </Button>
            </section>
            
                
        )
    }

    // render condition for featured items editor
    else if(isFeatureSelectOpen){
        return(
            <Accordion type="single"  collapsible className="flex flex-col w-full gap-2 pt-4" value={itemsOpen} onValueChange={setItemsOpen}>
                <div className="flex justify-center gap-4 items-center w-full">
                    <Button onClick={()=> setSelectionType("item")} className={`bg-transparent w-1/3 hover:cursor-pointer ease-in-out duration-200 ${selectionType == "item" && "bg-neutral-900"}`} variant={"outline"}>
                        Items
                    </Button>
                    <Button onClick={()=> {setSelectionType("list"); setItemsOpen("closed")}} className={`bg-transparent w-1/3 hover:cursor-pointer ease-in-out duration-200 ${selectionType == "list" && "bg-neutral-900"}`} variant={"outline"}>
                        List
                    </Button>
                </div>
                <div className="">
                    {
                        selectionType == "item" &&
                        <MediaSearch usage="featured" setIsMediaSearchOpen={setIsMediaSearchOpen}
                        onImageSelected={onFeaturedSelect} setFeaturedOpen={setItemsOpen} allItems={allFeatured}/>
                    }

                    {
                        selectionType == "list" &&
                        <SelectFeatured allFeatured={allFeatured} Formatter={resultToItem} itemSelected={onFeaturedSelect} setFeaturedOpen={setItemsOpen}/>
                    }
                </div>
                <AccordionItem value="open" className="mb-4 rounded-m  border-neutral-800/60">
                    <AccordionTrigger className="pl-2 hover:cursor-pointer">Featured items</AccordionTrigger>
                    <AccordionContent className="flex flex-col items-center gap-4 px-2">
                        
                        <DndContext 
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                        modifiers={[restrictToParentElement]}
                        >
                        <article className="flex flex-col w-full justify-center gap-2"> 
                        <SortableContext 
                            items={allFeatured.map(item => item.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {allFeatured.length > 0 && allFeatured.map((item,index)=>(
                                <FeaturedItem allItems={allFeatured} setAllItems={setAllFeatured} key={item.id} id={item.id} type={item.item_type} title={item.title} position={index + 1}  />
                            ))
                            }
                            {allFeatured.length < 1 && 
                                <p className="text-neutral-500 flex justify-center">No items</p>
                            }
                            
                        </SortableContext>
                         </article>
                        </DndContext>
                        
                       
                    </AccordionContent>
                </AccordionItem>

                <Button type="button" className="w-full hover:cursor-pointer" onClick={() => setIsFeatureSelectOpen(false)}>
                    <ArrowBigLeft className="w-7 h-7 hover:cursor-pointer"/>
                </Button>

            </Accordion>
           
            
        )
    }
}

interface MediaSearchProps {
    setIsMediaSearchOpen: (value: boolean) => void;
    onImageSelected: (imageUrl: string|QueryResult|featuredItem) => void;
    usage:"background"|"featured";
    setFeaturedOpen:(value:string) => void;
    allItems?:featuredItem[];
}

interface SelectFeaturedProps {
    itemSelected:(item:featuredItem) => void;
    setFeaturedOpen?:(value:string) => void;
    Formatter:(item:QueryResult, itemType:"item"|"list", position:number, 
        mediaType?: "movies" | "shows" | "songs" | "albums" | "artists") => featuredItem;
    allFeatured:featuredItem[];
}

function MediaSearch({ usage, setIsMediaSearchOpen, onImageSelected, setFeaturedOpen, allItems }: MediaSearchProps) {
    const [query, setQuery] = useState("");
    const [queryResults, setQueryResults] = useState<QueryResult[]>([]);
    const [responseLoading, setResponseLoading] = useState(false);
    const [listType, setListType] = useState("movies");
    const accessToken = localStorage.getItem("accessToken");
    const inputRef = useRef(null);

    const debounceQuery = useDebouncedCallback(
        (value) => {
            if(value.trim() == ""){
                setQueryResults([])
                return;
            }
            setQuery(value);

        },
        500
      );

    const fetchData = async () => {
            const TMDB_URL = "/tmdb/search";
            const SPOTIFY_URL = "/spotify/search";

            let queryPath = "";
            if(listType == "movies" || listType == "shows"){
                queryPath = TMDB_URL;
            }
            else if(listType == "songs" || listType == "artists" || listType == "albums"){
                queryPath = SPOTIFY_URL;
            } 
            else {
                return;
            }

            if(query.trim() == ""){
                return;
            }

            try{
                setResponseLoading(true);
                const res = await apiClient.get(queryPath ,{
                    headers: {
                    Authorization: `Bearer ${accessToken}`,
                    },
                    params: {
                        query: query,
                        list_type: listType
                    }
                })

                setResponseLoading(false);
                setQueryResults(res.data);
            }

            catch (err: AxiosError) {
                setResponseLoading(false);
            }
        }
    
    // if selecting background image
    const handleSelectImage = (url:string) => {
        onImageSelected(url);
    }

    // if selecting item for featuredItems
    const handleSelectItem = (item:QueryResult, itemType:"movies"|"shows"|"albums"|"songs") => {
        if(usage === "featured"){
            onImageSelected(resultToItem(item, "item", allItems?.length ?? 0 ,false,itemType))
            setQuery("");
            setQueryResults([]);
            setFeaturedOpen("open");
        }
    }

    useEffect(()=> {
        fetchData();
    }, [query])

    useEffect(() => {
        setQueryResults([]);
        if(query.trim() != ""){
            fetchData();
        }
    },[listType])
    
    
    return(
        <section className="flex flex-col gap-4 pt-4">
            <div className="flex gap-1 items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                    <Label htmlFor="image-search">Search</Label>
                    <Input
                        type="text"
                        ref={inputRef}
                        id="image-search"
                        defaultValue={query} 
                        placeholder="Search for an item"
                        className="w-full"
                        onChange={(e) => {
                            debounceQuery(e.target.value);
                            setFeaturedOpen("closed");
                        }}
                    />
                </div>
                <div className="flex flex-col items-center gap-2 w-30">
                    <Label htmlFor="image-search">Media type</Label>
                    <Select defaultValue={`${listType}`} onValueChange={value => {setListType(value); setFeaturedOpen("closed");}}>
                        <SelectTrigger className="">
                            <SelectValue placeholder="Media Type" />
                        </SelectTrigger>
                        <SelectContent className="dark">
                            <SelectItem value="albums">Albums</SelectItem>
                            <SelectItem value="shows">Shows</SelectItem>
                            <SelectItem value="movies">Movies</SelectItem>
                            <SelectItem value="songs">Songs</SelectItem>  
                        </SelectContent>
                    </Select>
                </div>
            </div>
                    {/*
                    name?: string;
                    title?: string;
                    overview?: string;
                    id: string;
                    backdrop_path?: string;
                    poster_path: string;
                    release_date?: Date;
                    first_air_date?: Date;
                    */}

            <ScrollArea className={`flex flex-col gap-2 ${queryResults.length > 0 ? " h-60 overflow-y-auto" : "hidden h-0"  }`}>
                {queryResults.map((result, index) => (
                    <>
                    { // if movies or shows
                        result.poster_path && (listType == "movies" || listType == "shows") &&

                        <div key={index} className="flex flex-row gap-2 w-full px-2 h-35">
                            <img src={"https://image.tmdb.org/t/p/w500/"+result.poster_path} alt={ listType == "movies" ? result.title : result.name} className="h-30 object-cover rounded-md" />
                            <div className="flex flex-col w-full justify-between my-5">
                                <h4 className="font-semibold flex w-full justify-center mt-5">{truncateText((listType == "movies" ? result.title : result.name), 40) }</h4>
                                <Button onClick={() => usage == "background" ? handleSelectImage("https://image.tmdb.org/t/p/w500/"+result.poster_path) : handleSelectItem(result,listType)} className="justify-self-end">Use</Button>
                            </div>
                            
                        </div>
                    }

                    { // if albums
                        result.images && result.images.length >= 1 && listType == "albums" &&

                        <div key={index} className="flex flex-row gap-2 w-full px-2 h-35">
                            <img src={result.images[0].url} alt={result.name} className="h-30 object-cover rounded-md" />
                            <div className="flex flex-col w-full justify-center items-center gap-2">
                                <div className="flex flex-col w-full gap-2justify-center items-center text-center">
                                    <h4 className="font-semibold text-sm">{truncateText(result.name, 30)}</h4>
                                    { result.artists[0] &&
                                            <p className="text-sm">{result.artists[0].name}</p>
                                    }
                                </div>
                                
                                <Button onClick={
                                    () => {
                                        usage == "background" ? handleSelectImage(result.images[0].url) : handleSelectItem(result,listType)
                                    }
                                }
                                    className="justify-self-end">Use</Button>
                            </div>
                            
                        </div>
                    }

                    { // if songs
                        result.album && result.album.images.length >= 1 && result.artists && listType == "songs" &&

                        <div key={index} className="flex flex-row gap-2 w-full px-2 h-35">
                            <img src={result.album.images[0].url} alt={result.name} className="h-30 object-cover rounded-md" />
                            <div className="flex flex-col w-full justify-center items-center gap-2">
                                <div className="flex flex-col w-full gap-2justify-center items-center text-center">
                                    <h4 className="font-semibold text-sm">{truncateText(result.name, 30)}</h4>
                                    { result.artists[0] &&
                                            <p className="text-sm">{result.artists[0].name}</p>
                                    }
                                </div>
                                
                                <Button onClick={() => usage === "background" ? handleSelectImage(result.album.images[0].url) : handleSelectItem(result,listType)} className="justify-self-end">Use</Button>
                            </div>
                            
                        </div>
                    }
                    
                    </>
                    
                ))}
            </ScrollArea>

            
        </section>
    )
    
}

function SelectFeatured({allFeatured, Formatter, itemSelected, setFeaturedOpen}:SelectFeaturedProps){
    const [query, setQuery] = useState("");
    const [queryResults, setQueryResults] = useState([]);
    const {user, setUser} = useContext(UserContext);

    const username = user.username || localStorage.getItem("username");

    const {isPending, isError, data, error, isSuccess } = useQuery({
        queryKey: ['profileList', username],
        queryFn: () => FetchUserLists(username),
        enabled: !!username,
    })

    useEffect(()=> {
       
        if(data){
            setQueryResults(data);
        }
        
    },[data])

    if(!username){
        console.log(user)
        return( 
            <article className="flex flex-col justify-center items-center mt-2 gap-2 text-neutral-400 w-full text-sm text-center">
                <p>
                    We ran into an issue loading your lists. Try again later
                </p>
                <SearchX />
            </article>
        )
    }

    return (
        <section className="flex flex-col w-full h-full">
        <form className="flex items-center justify-center gap-1 pt-4">
            <div className="flex flex-col  items-center gap-2">
                <Label htmlFor="list-search">Search</Label>
                <Input
                    type="text"
                    id="list-search" 
                    placeholder="Search for a list"
                    className="w-full"
                    onChange={(e) => {
                        if(e.currentTarget.value.trim() == ""){
                            setQueryResults(data);
                            return;
                        }
                        if(data && isSuccess){
                            setQueryResults(data.filter((item,index)=> item.title.includes(e.target.value.trim())))
                        }
                        setFeaturedOpen("closed");
                    }}
                />
            </div>
        </form>
        <Label className="flex mt-5 pl-4">Your lists</Label>
        <ScrollArea className=" w-full h-30 pt-5 px-2">
                <main className="flex flex-col gap-2 justify-center items-center">
                {
                    isError &&
                    <article className="flex flex-col items-center gap-2 text-neutral-400 max-w-1/2 text-sm text-center">
                        <p>
                        We ran into an issue loading your lists. Try again later
                        </p>
                        <SearchX />
                    </article>
                }
                {
                    isPending && 
                    <SecondaryLoader />
                }
                {
                    isSuccess && queryResults.length > 0 &&
                        queryResults.filter(item => !allFeatured.some(f => 
                        f.item_type === "list" && f.internal_list_id === item.id
                        )).map((item,index)=> 
                        <article key={item.id} className="flex justify-between items-center p-2 min-h-10 w-full border-b-1 border-neutral-800/80 hover:bg-neutral-900/40 rounded-sm">
                            <section className="flex gap-2 max-w-2/3 items-center">
                                <span className="w-fit"><ItemTypeIcon type={item.list_type} /></span>
                                <h3 className="text-white text-sm ml-2">
                                    {item.title}
                                </h3>
                            </section>
                            <SquarePlus onClick={() => itemSelected(Formatter(item,"list", allFeatured.length))} className="hover:cursor-pointer" />
                        </article>
                    )
                }
                {
                    isSuccess && queryResults.length == 0 &&
                        <article className="flex justify-center px-2 min-h-10 w-full rounded-sm">
                                <h3 className="text-white text-sm ml-2">
                                    You have no lists. Create one!
                                </h3>
                        </article>
                    
                }
            </main>
        </ScrollArea>
        </section>
    )
}

interface ItemTypeIconProps {
    type:string;
}

function ItemTypeIcon({type}:ItemTypeIconProps){
    const classes = "w-4 h-4"

    switch (type) {
        case "albums":
            return <Disc3Icon className={"text-white " + classes} />;
            break;
        case "movies":
            return <Clapperboard className={"text-white "+ classes} />;
            break;
        case "shows":
            return <Tv className={"text-white "+ classes} />;
            break;
        case "songs":
            return <Music2 className={"text-white "+ classes} />;
        case "artists":
            return <Mic2 className={"text-white "+ classes} />;
        case "list":
            return <ListOrdered className={"text-white "+ classes} />;
        default:
            return null;
            break;
    }
}

interface FeaturedItemProps{
    id:string;
    title:string;
    type:string;
    position:number;
    allItems: featuredItem[];
    setAllItems: (itemArray:featuredItem[]) => void;
}

function FeaturedItem({id, title, type, position, allItems, setAllItems}:FeaturedItemProps) {
    // dnd defaults
    const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            } = useSortable({id, transition: {
                duration: 300, // milliseconds
                easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
            }});
    
    const style = {
        transform: DNDCSS.Transform.toString(transform),
        transition
        };

    const handleDelete = () => {
        setAllItems(allItems.filter(item => item.id !== id));
    }
    

    return (
        <div ref={setNodeRef} style={style} key={position} className="flex relative items-center min-h-12 p-2 gap-0.5 text-neutral-400 border-b-1
                                border-neutral-800/80 hover:bg-neutral-900 bg-neutral-950 rounded-sm cursor-default">
            <div {...attributes} {...listeners} className="flex justify-center items-center h-full w-fit hover:cursor-grab active:cursor-grabbing">
                <GripVertical className="w-5 h-5 " /> 
            </div>
            
            <div className="flex items-center justify-between text-sm pl-0.5 pr-2 wrap-break-word gap-2 w-full h-full ">
                <div className="flex gap-2 items-center">
                    <ItemTypeIcon type={type}/>
                    <h3 className="text-white">{title} </h3>
                </div>
                <p className="pr-1">{position}</p>
                
            </div>
            <div className="flex justify-center items-center h-full w-fit ">
                <X className="w-4 h-4 hover:cursor-pointer duration-150 ease-in-out hover:text-white hover:w-5 hover:h-5" 
                    onClick={() => handleDelete()}
                /> 
            </div>
            
            
        </div>
    )
}
// helper formatter from result type to featuredItem type
const resultToItem = (
    item:unknown,
    resultType: "list" | "item",
    position:number,
    fromSync?: boolean,
    mediaType?: "movies" | "shows" | "songs" | "albums" | "artists",
    ): featuredItem => {
    // if typical use case 
    if (resultType === "item" && !fromSync) {
        const result = item as QueryResult;
        switch (mediaType) {
            case "movies":
            case "shows":
                return ({
                    id: (result.id?.toString() ?? crypto.randomUUID()), // assign unique id for dnd
                    item_type: mediaType,
                    internal_list_id: null,
                    external_id: result.id?.toString() ?? null,
                    title: (result.title || result.name) ?? "",
                    image: result.poster_path
                        ? `https://image.tmdb.org/t/p/w500/${result.poster_path}`
                        : "",
                    year: result.release_date
                        ? Number(String(result.release_date).slice(0, 4))
                        : result.first_air_date
                        ? Number(String(result.first_air_date).slice(0, 4))
                        : null,
                    comment: null,
                    order:position
                    })

            case "albums":
                return ({
                    id: (result.id?.toString() ?? crypto.randomUUID()),
                    item_type: mediaType,
                    internal_list_id: null,
                    external_id: result.id?.toString() ?? null,
                    title: result.name ?? "",
                    image: result.images?.[0]?.url ?? "",
                    year: result.release_date
                        ? Number(String(result.release_date).slice(0, 4))
                        : null,
                    comment: null,
                    order:position,
                    artist: result.artists?.[0]?.name ?? null
                });
            case "songs":
                return ({
                    id: (result.id?.toString() ?? crypto.randomUUID()),
                    item_type: mediaType,
                    internal_list_id: null,
                    external_id: result.id?.toString() ?? null,
                    title: result.name ?? "",
                    image: result.album?.images?.[0]?.url ?? "",
                    year: result.album?.release_date
                        ? Number(String(result.album.release_date).slice(0, 4))
                        : null,
                    comment: null,
                    order:position,
                    artist: result.artists?.[0]?.name ?? null
                   
                });
            case "artists":
                return ({
                    id: (result.id?.toString() ?? crypto.randomUUID()),
                    item_type: mediaType,
                    internal_list_id: null,
                    external_id: result.id?.toString() ?? null,
                    title: result.name ?? "",
                    image: result.images?.[0]?.url ?? "",
                    year: null,
                    comment: null,
                    order:position
                    
                });
            default:
                throw new Error("Unsupported media type");
        }
    }
    // if from profile context 
    else if (resultType === "item" && fromSync) {
        const result = item as ProfileFeaturedType;
        return ({
            id: String(result.id), // assign unique id for dnd
            item_type: mediaType,
            internal_list_id: null,
            external_id: result.external_id?.toString(),
            title: (result.title || result.name) ?? "",
            image: result.image ?? "",
            year: Number(result.year) ?? null,
            comment: result.comment ?? null,
            order:position,
            artist: result.artist ?? null,
            })     
    }
    
    if(resultType == "list" && !fromSync){
        const result = item as QueryResult;
        return({
            id: (result.id?.toString() ?? crypto.randomUUID()),
            item_type: "list",
            internal_list_id: Number(result.id?.toString()) ?? null,
            title: (result.title || result.name) ?? "",
            comment: null,
            order:position
        })
    }
    else if(resultType == "list" && fromSync){
        const result = item as ProfileFeaturedType;
        return({
            id: result.id ?? null,
            item_type: "list",
            internal_list_id: Number(result.internal_list_details?.id) ?? null,
            title: (result.title || result.name) ?? "",
            comment: null,
            order:position
        })
    }  
    throw new Error("Unsupported resultType");
    } 